import { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Toggle, NoPaymentMethodCard, PaymentMethodCard } from "@common";
import { useSpinnerContext, useSessionContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import { helpers, Enums } from "@shared";
import { PaymentDay } from ".";
import type { AccountSettings } from '@app-types';
import { PostHogEvents, capturePosthogEvent } from '@analytics';

type SettingsMap = Record<number, AccountSettings>;

const SetupAutopayToggled = () => {

    const navigate = useNavigate(), location = useLocation();
    const { setSpinnerShow } = useSpinnerContext();
    const [settings, setSettings] = useState<SettingsMap | null>(null);
    const {
        session,
        accounts,
        changeSelectedResident
    } = useSessionContext();
    const {
        orgDays,
        potDeadline
    } = session || {};

    const getPayorPaymentMethods = async () => {
        setSpinnerShow(true);
        try {
            const newSettings: SettingsMap = {};
            const response = await sunboundPayProxy.getPayorPaymentMethods({ isActive: true, withAccountAssignments: true });

            response.forEach(a => {
                const {
                    idPaymentType,
                    accountPaymentMethods = [],
                    sourceName,
                    accountType,
                    last4,
                    disabledAt
                } = a;
                accountPaymentMethods.forEach(apm => {
                    const {
                        idPaymentMethod,
                        isDefault,
                        hasPOTInvoice,
                        accountPayor: {
                            isAutoPay,
                            dueDay,
                            account: {
                                residentName,
                                idAccount
                            }
                        }
                    } = apm;
                    if (isDefault) {
                        newSettings[idAccount] = {
                            idPaymentMethod,
                            idPaymentType,
                            isAutoPay,
                            dueDay,
                            sourceName,
                            accountType,
                            last4,
                            residentName,
                            hasPOTInvoice,
                            disabledAt
                        };
                    }
                });
            });

            setSettings(newSettings);
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    useEffect(() => {
        (async () => {
            await getPayorPaymentMethods();
        })();
    }, [session]);

    const toggleAutoPay = async (idAccount: number) => {
        if (!settings || !orgDays) return;

        setSpinnerShow(true);
        try {
            let autodrawDay: number | null = null;
            const newSettings: SettingsMap = { ...settings };
            const accountSettings = newSettings[idAccount];
            if (!accountSettings) return; // For TS compiler; idAccount should always exist in settings

            const { isAutoPay, dueDay: payorDueDay } = accountSettings;
            const flipAutoPay = !isAutoPay;

            if (flipAutoPay)
                ({ autodrawDay } = helpers.getDaysFromPayor(orgDays, payorDueDay));

            await sunboundPayProxy.updateAccountPayorPreferences(idAccount, { isAutoPay: flipAutoPay, dueDay: autodrawDay });

            accountSettings.isAutoPay = flipAutoPay;
            accountSettings.dueDay = autodrawDay;
            setSettings(newSettings);
            capturePosthogEvent(PostHogEvents.AUTOPAY_TOGGLED, { enabled: flipAutoPay });
        } catch (error) {
            console.error(error);
        } finally {
            setSpinnerShow(false);
        }
    };

    const onClick = () => {
        if (settings) {
            const enabledCount = Object.values(settings).filter(s => s.isAutoPay).length;
            capturePosthogEvent(PostHogEvents.AUTOPAY_CONFIGURED, {
                accountsWithAutopay: enabledCount,
                totalAccounts: Object.keys(settings).length
            });
        }
        helpers.navigateNext(location, navigate);
    };

    const onSkip = () => {
        helpers.navigateNext(location, navigate);
    };

    // Check if at least one autopay is enabled
    const hasAnyAutopayEnabled = settings && Object.values(settings).some((s) => s.isAutoPay);

    const dueDayChange = async (idAccount: number, dueDay: number) => {
        if (!settings) return;

        setSpinnerShow(true);
        try {
            const newSettings: SettingsMap = { ...settings };
            const accountSettings = newSettings[idAccount];
            if (!accountSettings) return; // For TS compiler; idAccount should always exist in settings

            await sunboundPayProxy.updateAccountPayorPreferences(idAccount, { dueDay });
            accountSettings.dueDay = dueDay;
            setSettings(newSettings);
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const paymentDay = (idAccount: number) => {
        if (!settings || !orgDays) return null;

        const accountSettings = settings[idAccount];
        if (!accountSettings) return null; // For TS compiler; idAccount should always exist in settings

        const { dueDay: payorDueDay, hasPOTInvoice, idPaymentType } = accountSettings;
        const { locked, autodrawDay, maxDay } = helpers.getDaysFromPayor(orgDays, payorDueDay, hasPOTInvoice ? Enums.ProductType.SunboundPay : null, potDeadline);
        const isACH = Number(idPaymentType) === Enums.PaymentType.ACH;

        return (
            <PaymentDay setDueDay={(dueDay) => dueDayChange(idAccount, dueDay)} dueDay={autodrawDay} locked={locked} maxDay={maxDay} isACH={isACH} id="autopay-day-selector" />
        );
    };

    const renderAccounts = settings && accounts.map(account => {
        const { idAccount, residentName } = account;
        const accountSettings = settings[idAccount];
        const isAutoPay = !!accountSettings?.isAutoPay;
        const isToggleEnabled = !!((accountSettings && !accountSettings.disabledAt) || isAutoPay);

        return (
            <div key={idAccount} className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-col gap-4">
                {/* Header row with toggle */}
                <div className="flex items-center justify-between">
                    <span className="text-base leading-6 font-medium text-neutral-950 tracking-[0.16px]">
                        {residentName}
                    </span>
                    <Toggle
                        isOn={isAutoPay}
                        enabled={isToggleEnabled}
                        onClick={() => toggleAutoPay(idAccount)}
                    />
                </div>

                {/* Content based on autopay state */}
                {!isAutoPay && accountSettings && (
                    <PaymentMethodCard
                        paymentMethod={accountSettings}
                        isDefault
                        onChange={() => changeSelectedResident(idAccount, navigate, `/payment-method-settings`)}
                    />
                )}

                {!isAutoPay && !accountSettings && (
                    <NoPaymentMethodCard
                        onAddPaymentMethod={() => changeSelectedResident(idAccount, navigate, `/payment-method-settings`)}
                    />
                )}

                {isAutoPay && (
                    paymentDay(idAccount)
                )}
            </div>
        );
    });

    return (
        <div className="flex flex-col w-full">
            {/* Header */}
            <div className="border-b border-neutral-100 pb-6 mb-8">
                <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">
                    Autopay Settings
                </h1>
            </div>

            {/* Instruction text */}
            <p className="text-sm leading-5 font-normal text-neutral-800 tracking-[0.14px] mb-8">
                Make sure your Autopay settings are set up accurately.
            </p>

            {/* Account cards */}
            <div className="flex flex-col gap-4 mb-8">{renderAccounts}</div>

            {/* Action buttons */}
            <div className="flex flex-col md:flex-row-reverse gap-4 w-full">
                <Button
                    type="button"
                    variant="primary"
                    size="lg"
                    className="w-full md:flex-1"
                    onClick={onClick}
                    disabled={!hasAnyAutopayEnabled}
                >
                    Continue
                </Button>
                <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    className="w-full md:flex-1"
                    onClick={onSkip}
                >
                    Skip for Now
                </Button>
            </div>
        </div>
    );
};

export default SetupAutopayToggled;
